import type { LiveEvent, LivePlatformAdapter } from "./protocol";

export type DouyinMessageType = "live_comment" | "live_like" | "live_gift" | "live_fansclub";

export type DouyinPayload = {
  msg_id: string;
  sec_openid?: string;
  sec_open_id?: string;
  content?: string;
  like_num?: number;
  sec_gift_id?: string;
  gift_num?: number;
  gift_value?: number;
  avatar_url?: string;
  nickname?: string;
  timestamp: number;
  test?: boolean;
  fansclub_reason_type?: number;
  fansclub_level?: number;
};

export type DouyinCallbackEnvelope = {
  headers: {
    "x-nonce-str": string;
    "x-timestamp": string;
    "x-signature": string;
    "x-roomid": string;
    "x-msg-type": DouyinMessageType;
    "content-type": "application/json";
  };
  body: DouyinPayload[];
};

export type DouyinAdapterInput = Pick<DouyinCallbackEnvelope, "body"> & {
  headers: Pick<DouyinCallbackEnvelope["headers"], "x-msg-type" | "x-roomid">;
};

export const douyinLiveAdapter: LivePlatformAdapter<DouyinAdapterInput> = {
  platform: "douyin",
  normalize: ({ headers, body }) => normalizeDouyinBatch(headers["x-msg-type"], headers["x-roomid"], body),
};

export function normalizeDouyinBatch(
  messageType: string,
  roomId: string,
  body: unknown,
): LiveEvent[] {
  if (!isDouyinMessageType(messageType) || !Array.isArray(body)) return [];
  return body.map((value) => normalizeDouyinPayload(messageType, roomId, value)).filter((value): value is LiveEvent => value !== null);
}

export function normalizeDouyinPayload(
  messageType: DouyinMessageType,
  roomId: string,
  value: unknown,
): LiveEvent | null {
  if (!value || typeof value !== "object") return null;
  const payload = value as Record<string, unknown>;
  const messageId = stringField(payload.msg_id);
  const openId = stringField(payload.sec_openid ?? payload.sec_open_id);
  const timestamp = timestampMs(payload.timestamp);
  if (!messageId || !openId || timestamp === null) return null;
  const base = {
    id: messageId,
    roomId,
    user: {
      id: openId,
      displayName: stringField(payload.nickname) || "直播观众",
    },
    at: timestamp,
  };
  if (messageType === "live_comment") {
    const text = stringField(payload.content).slice(0, 10);
    return text ? { ...base, type: "comment", text } : null;
  }
  if (messageType === "live_like") {
    return { ...base, type: "like", count: positiveInt(payload.like_num, 10_000) };
  }
  if (messageType === "live_gift") {
    const giftId = stringField(payload.sec_gift_id) || "unknown-gift";
    return {
      ...base,
      type: "gift",
      giftId,
      giftName: `抖音礼物 ${giftId.slice(-6)}`,
      count: positiveInt(payload.gift_num, 100),
      value: positiveInt(payload.gift_value, 1_000_000),
    };
  }
  return { ...base, type: "follow" };
}

export function createMockDouyinPayload(
  messageType: DouyinMessageType,
  user: { id: string; displayName: string },
  fields: Partial<DouyinPayload> = {},
): DouyinPayload {
  mockSequence += 1;
  return {
    msg_id: `mock-douyin-${Date.now()}-${mockSequence}`,
    sec_openid: user.id,
    avatar_url: "",
    nickname: user.displayName,
    timestamp: Date.now(),
    ...(messageType === "live_comment" ? { content: "跳" } : {}),
    ...(messageType === "live_like" ? { like_num: 1 } : {}),
    ...(messageType === "live_gift" ? { sec_gift_id: "mock-rescue-whistle", gift_num: 1, gift_value: 100 } : {}),
    ...(messageType === "live_fansclub" ? { fansclub_reason_type: 2, fansclub_level: 1 } : {}),
    ...fields,
  };
}

export function isDouyinMessageType(value: string): value is DouyinMessageType {
  return value === "live_comment" || value === "live_like" || value === "live_gift" || value === "live_fansclub";
}

let mockSequence = 0;

function stringField(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function positiveInt(value: unknown, max: number): number {
  const number = Number(value ?? 1);
  return Number.isFinite(number) ? Math.min(max, Math.max(1, Math.floor(number))) : 1;
}

function timestampMs(value: unknown): number | null {
  const number = Number(value);
  if (!Number.isFinite(number) || number <= 0) return null;
  return number < 10_000_000_000 ? number * 1_000 : number;
}
